import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./Employee.css";
// const url = import.meta.env.VITE_BACKEND_URL;
const url = process.env.REACT_APP_BACKEND_URL;

const EmployeeFormWizard = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [departments, setDepartments] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]); // store file object
  };

  const [formData, setFormData] = useState({
    // Personal Information
    fullname: "",
    gender: "",
    marital_status: "",
    phone: "",
    dob: "",
    pob: "",
    email: "",
    nationality: "",
    blood: "",
    next_of_kin_name: "",
    next_of_kin_contact: "",
    photo: "",

    // Education - now an array to support multiple entries
    education: [
      {
        degree: "",
        institution: "",
        field: "",
        dateGraduated: "",
        gpa: "",
        grade: ""
      }
    ],

    // Employment fields (moved to root level to match backend schema)
    joined_date: "",
    obtained_date: "",
    contract_type: "",
    employee_type_id: "", // Changed from employee_type to employee_type_id
    faculty_id: "",
    salary: ""
  });

  // Validation functions for each step
  const validateStep1 = () => {
    const personalFields = [
      formData.fullname,
      formData.gender,
      formData.marital_status,
      formData.phone,
      formData.dob,
      formData.pob,
      formData.email,
      formData.nationality,
      formData.blood,
      formData.next_of_kin_name,
      formData.next_of_kin_contact
    ];
    
    return personalFields.every(field => field && field.trim() !== "");
  };

  const validateStep2 = () => {
    return formData.education.every(edu => 
      edu.degree && 
      edu.degree.trim() !== "" && 
      edu.institution && 
      edu.institution.trim() !== "" && 
      edu.field && 
      edu.field.trim() !== "" && 
      edu.dateGraduated && 
      edu.dateGraduated.trim() !== ""
    );
  };

  const validateStep3 = () => {
    const employmentFields = [
      formData.joined_date,
      formData.contract_type,
      formData.employee_type_id,
      formData.faculty_id,
      formData.salary
    ];
    
    return employmentFields.every(field => field && field.trim() !== "");
  };

  // Helper function to extract array from API response
  const extractArrayFromResponse = (responseData) => {
    // Handle different possible response structures
    if (Array.isArray(responseData)) {
      return responseData;
    } else if (responseData && Array.isArray(responseData.data)) {
      return responseData.data;
    } else if (responseData && responseData.data && Array.isArray(responseData.data.result)) {
      return responseData.data.result;
    } else if (responseData && responseData.result && Array.isArray(responseData.result)) {
      return responseData.result;
    } else {
      console.warn("Unexpected API response structure:", responseData);
      return [];
    }
  };

  // Fetch departments and employee types from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [deptResponse, typeResponse] = await Promise.all([
          axios.get(`${url}/api/department`),
          axios.get(`${url}/api/employeetype`)
        ]);

        // Extract arrays from API responses
        const deptData = extractArrayFromResponse(deptResponse.data);
        const typeData = extractArrayFromResponse(typeResponse.data);

        setDepartments(deptData);
        setEmployeeTypes(typeData);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load department and employee type data");
        // Set default options in case of error
        setDepartments([
          { _id: "it", name: "Information Technology" },
          { _id: "hr", name: "Human Resources" },
          { _id: "finance", name: "Finance" },
          { _id: "marketing", name: "Marketing" }
        ]);
        setEmployeeTypes([
          { _id: "regular", name: "Regular" },
          { _id: "contractor", name: "Contractor" },
          { _id: "intern", name: "Intern" }
        ]);
      }
    };

    fetchData();
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("education.")) {
      const parts = name.split(".");
      const index = parseInt(parts[1]);
      const field = parts[2];

      const updatedEducation = [...formData.education];
      updatedEducation[index] = {
        ...updatedEducation[index],
        [field]: value
      };

      setFormData({
        ...formData,
        education: updatedEducation
      });
    } else {
      // Handle all other fields at root level
      setFormData({ ...formData, [name]: value });
    }
  };

  // Add a new education entry
  const addEducation = () => {
    setFormData({
      ...formData,
      education: [
        ...formData.education,
        {
          degree: "",
          institution: "",
          field: "",
          dateGraduated: "",
          gpa: "",
          grade: ""
        }
      ]
    });
  };

  // Remove an education entry
  const removeEducation = (index) => {
    if (formData.education.length <= 1) return;

    const updatedEducation = [...formData.education];
    updatedEducation.splice(index, 1);

    setFormData({
      ...formData,
      education: updatedEducation
    });
  };

  // Navigation functions with validation
  const nextStep = () => {
    let isValid = false;
    
    switch (step) {
      case 1:
        isValid = validateStep1();
        if (!isValid) {
          setError("Please complete all Personal Information fields before proceeding.");
          return;
        }
        break;
      case 2:
        isValid = validateStep2();
        if (!isValid) {
          setError("Please complete all Educational Background fields before proceeding.");
          return;
        }
        break;
      default:
        break;
    }
    
    setError(""); // Clear any previous errors
    setStep(step + 1);
  };

  const prevStep = () => {
    setError(""); // Clear errors when going back
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Validate step 3 before submission
    if (!validateStep3()) {
      setError("Please complete all Employment Information fields before submitting.");
      setLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        if (key === "education") {
          formData.education.forEach((edu, idx) => {
            Object.keys(edu).forEach((field) => {
              formDataToSend.append(`education[${idx}][${field}]`, edu[field]);
            });
          });
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Append photo if selected
      if (file) {
        formDataToSend.append("photo", file);
      }

      const response = await axios.post(`${url}/api/employee`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (response.status === 200 || response.status === 201) {
        setSuccess("Employee Registered Successfully!");
        setFormData({
          fullname: "",
          gender: "",
          marital_status: "",
          phone: "",
          dob: "",
          pob: "",
          email: "",
          nationality: "",
          blood: "",
          next_of_kin_name: "",
          next_of_kin_contact: "",
          photo: "",
          education: [
            {
              degree: "",
              institution: "",
              field: "",
              dateGraduated: "",
              gpa: "",
              grade: ""
            }
          ],
          joined_date: "",
          obtained_date: "",
          contract_type: "",
          employee_type_id: "",
          faculty_id: "",
          salary: ""
        });
        setFile(null);
        setStep(1);
      }
    } catch (err) {
      console.error("Full error details:", err);
      setError("Error: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wizard-container">

      <button
        onClick={() => navigate("/employeelist")}
        className="view-employees-btn"
      >
        View Employees
      </button>
      <h1>Add New Employee</h1>

      {/* Status Messages */}
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Stepper */}
      <div className="steps">
        <div className={`step ${step === 1 ? "active" : ""}`}>1 Personal</div>
        <div className={`step ${step === 2 ? "active" : ""}`}>2 Education</div>
        <div className={`step ${step === 3 ? "active" : ""}`}>3 Employment</div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Step 1: Personal Information */}
        {step === 1 && (
          <div className="form-step">
            <h2>Personal Information</h2>
            <div className="form-row">
              <input
                type="text"
                name="fullname"
                placeholder="Full Name"
                value={formData.fullname}
                onChange={handleChange}
                autoComplete="off"
                required
              />
              <div className="form-row">
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required
                >
                  <option value="">Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                <select
                  name="marital_status"
                  value={formData.marital_status}
                  onChange={handleChange}
                  required
                >
                  <option value="">Marital Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                </select>
              </div>

            </div>
            <div className="form-row">
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                required
              />
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="pob"
                placeholder="Place of Birth"
                value={formData.pob}
                onChange={handleChange}
                required
              />
            </div>


            <div className="form-row">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="off"
                required
              />

              <input
                type="text"
                name="nationality"
                placeholder="nationality"
                value={formData.nationality}
                onChange={handleChange}
                required
              />
              <div className="form-row">

                <select
                  name="blood"
                  value={formData.blood}
                  onChange={handleChange}
                  required
                >
                  <option value="">Blood Type</option>
                  <option value="O+">O+</option>
                  <option value="A+">A+</option>
                  <option value="B+">B+</option>
                  <option value="AB+">AB+</option>
                  <option value="O-">O-</option>
                  <option value="A-">A-</option>
                  <option value="B-">B-</option>
                  <option value="AB-">AB-</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <input
                type="text"
                name="next_of_kin_name"
                placeholder="next of kin name"
                value={formData.next_of_kin_name}
                onChange={handleChange}
                autoComplete="off"
                required
              />

              <input
                type="text"
                name="next_of_kin_contact"
                placeholder="next of kin contact"
                value={formData.next_of_kin_contact}
                onChange={handleChange}
                required
              />
              <div className="form-row">

                <input
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                />


              </div>
            </div>
          </div>

        )}

        {/* Step 2: Education */}
        {step === 2 && (
          <div className="form-step">
            <h2>Educational Background</h2>

            {formData.education.map((edu, index) => (
              <div key={index} className="education-item">
                <div className="form-row">
                  <select
                    name={`education.${index}.degree`}
                    value={edu.degree}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Education degree</option>
                    <option value="Primary">Primary</option>
                    <option value="High School">High School</option>
                    <option value="Bachelor">Bachelor</option>
                    <option value="Master">Master</option>
                    <option value="PhD">PhD</option>
                  </select>
                  <input
                    type="text"
                    name={`education.${index}.institution`}
                    placeholder="Institution Name"
                    value={edu.institution}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-row">
                  <input
                    type="text"
                    name={`education.${index}.field`}
                    placeholder="Field of Study"
                    value={edu.field}
                    onChange={handleChange}
                    required
                  />
                  <input
                    type="date"
                    name={`education.${index}.dateGraduated`}
                    placeholder="Graduation Year"
                    value={edu.dateGraduated}
                    onChange={handleChange}
                    min="1900"
                    max="2030"
                    required
                  />
                </div>
                <div className="form-row">
                  <input
                    type="text"
                    name={`education.${index}.gpa`}
                    placeholder="GPA"
                    value={edu.gpa}
                    onChange={handleChange}
                  />
                  <input
                    type="text"
                    name={`education.${index}.grade`}
                    placeholder="grade"
                    value={edu.grade}
                    onChange={handleChange}
                  />
                  {formData.education.length > 1 && (
                    <button
                      type="button"
                      className="remove-btn"
                      onClick={() => removeEducation(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}

            <button
              type="button"
              className="add-btn"
              onClick={addEducation}
            >
              <i className="fas fa-plus"></i> Add Education
            </button>
          </div>
        )}

        {/* Step 3: Employment */}
        {step === 3 && (
          <div className="form-step">
            <h2>Employment Information</h2>
            <div className="form-row">
              <input
                type="date"
                name="joined_date"
                placeholder="Joined Date"
                value={formData.joined_date}
                onChange={handleChange}
                required
              />
              <input
                type="date"
                name="obtained_date"
                placeholder="Obtained Date"
                value={formData.obtained_date}
                onChange={handleChange}
              />
            </div>
            <div className="form-row">
              <select
                name="contract_type"
                value={formData.contract_type}
                onChange={handleChange}
                required
              >
                <option value="">Contract Type</option>
                <option value="Full Time">Full Time</option>
                <option value="Part Time">Part Time</option>
              </select>
              <select
                name="employee_type_id"
                value={formData.employee_type_id}
                onChange={handleChange}
                required
              >
                <option value="">Employee Type</option>
                {employeeTypes.length > 0 ? (
                  employeeTypes.map(type => (
                    <option key={type._id} value={type._id}>
                      {type.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Loading employee types...</option>
                )}
              </select>
            </div>
            <div className="form-row">
              <select
                name="faculty_id"
                value={formData.faculty_id}
                onChange={handleChange}
                required
              >
                <option value="">Department</option>
                {departments.length > 0 ? (
                  departments.map(dept => (
                    <option key={dept._id} value={dept._id}>
                      {dept.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Loading departments...</option>
                )}
              </select>
              <input
                type="number"
                name="salary"
                placeholder="Salary"
                value={formData.salary}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="form-navigation">
          {step > 1 && (
            <button type="button" onClick={prevStep} disabled={loading}>
              Back
            </button>
          )}
          {step < 3 && (
            <button type="button" onClick={nextStep} disabled={loading}>
              Next
            </button>
          )}
          {step === 3 && (
            <button type="submit" disabled={loading}>
              {loading ? "Submitting..." : "Submit"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default EmployeeFormWizard;